import { useCallback, useEffect, useMemo, useState } from "react";
import { EnvVariable, EnvVariableType, environmentTypesOptions } from "../../models/Environment"
import { Button, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { Form, Input, InputNumber, Select, Flex, Col, Avatar, UploadProps, Button as AntButton, Switch } from "antd";
import TextArea from "antd/es/input/TextArea";
import { FormProps, Rule } from "antd/es/form";
import ScriptEditor from "../common/ScriptEditor";
import { accessLevelOptions, getBase64EncodedImage } from "../../utils";
import './AssetEditForm.scss';
import Upload, { UploadFile } from "antd/es/upload";
import { useToasts } from "../../contexts/toasts";
import { v4 } from "uuid";
import { useSelector } from "react-redux";
import { sectionLabelsSelector } from "../../slices/sectionsSlice";
import { RootState } from "../../store";

type Props = {
  environmentVariable?: EnvVariable;
  onSubmit?: (environment: EnvVariable) => void;
  onCancel?: () => void;
}

const formLayout: FormProps = {
  layout: "vertical",
  colon: false,
}

export default function EnvironmentVariableForm({ environmentVariable, onSubmit, onCancel }: Props) {
  const { showToast } = useToasts();
  const [form] = Form.useForm();
  const [imageUrl, setImageUrl] = useState('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const variableLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "environment-variables"));

  const uploadProps = useMemo<UploadProps>(() => ({
    name: "imageUrl",
    beforeUpload: (file) => {
      const isImageFile = file.type.startsWith('image/');
      if (!isImageFile) {
        showToast('file-type-error', 'Only images are allowed to be uploaded', null, 'warning');
        return Upload.LIST_IGNORE;
      }
      const maxFileSize = window.__env.MAX_IMAGE_SIZE || 8;
      const isTooLarge = file.size / 1024 > maxFileSize; // size in KB
      if (isTooLarge) {
        showToast('image-size-error', `File size is too large. Image should be smaller than ${maxFileSize}KB`, null, 'warning');
        return Upload.LIST_IGNORE;
      }
      return false;
    },
    onChange: async ({ fileList }) => {
      if (fileList.length) {
        const latestFile = fileList[fileList.length - 1];
        const base64String = await getBase64EncodedImage(latestFile.originFileObj!)
        setImageUrl(base64String);
        form.setFieldValue("img", base64String);
        setFileList([latestFile]);
      } else {
        setImageUrl("");
        form.setFieldValue("img", null);
        setFileList([]);
      }
    },
  }), [form, showToast]);

  useEffect(() => {
    form.setFieldsValue(environmentVariable);
    if (environmentVariable && environmentVariable.type === EnvVariableType.Number) {
      form.setFieldValue("value", +environmentVariable.value);
    }
    if (environmentVariable && environmentVariable.img && environmentVariable.img !== "") {
      const base64Image = environmentVariable.img;
      const file: UploadFile = {
        uid: v4(),
        name: environmentVariable.name,
        status: 'done',
        url: base64Image,
      };
      setFileList([file]);
      setImageUrl(environmentVariable.img);
    }
  }, [environmentVariable, form]);

  const renderInputComponent = useCallback((type: string) => {
    switch (type) {
      case 'number':
        return <InputNumber className="w-100" min={1} placeholder={`${variableLabels.singular} value`} />;
      case 'string':
        return <Input placeholder={`${variableLabels.singular} value`} />;
      case 'email':
        return <Input type="email" placeholder={`${variableLabels.singular} value`} />;
      case 'token':
        return <TextArea cols={24} rows={5} placeholder={`${variableLabels.singular} value`} />;
      default:
        return <Input placeholder={`${variableLabels.singular} value`} />;
    }
  }, [variableLabels]);

  const getInputRules = useCallback((type: EnvVariableType): Rule[] | undefined => {
    switch (type) {
      case EnvVariableType.JSON: return;
      case EnvVariableType.Number:
        return [{ type: 'number', message: 'Invalid Number' }];
      case EnvVariableType.String:
      default:
        return [{ type: 'string', message: 'Invalid String' }];
      case EnvVariableType.Email:
        return [{ type: 'email', message: 'Invalid Email' }];
    }
  }, []);

  const handleScriptChange = useCallback((value?: string) => form.setFieldValue('value', value), [form]);

  return (
    <Form className="compact-form" form={form} {...formLayout} onFinish={onSubmit}>
      <Modal.Body>
        <Flex align="center">
          <Col span={6} className="text-center pe-2">
            <Avatar
              src={imageUrl}
              icon={<FontAwesomeIcon icon={solid('image')} size="xl" />}
              className="border"
              style={{ height: 80, width: 80 }}
            />
          </Col>
          <Col flex="auto">
            <Form.Item
              name="name"
              label="Name"
              rules={[
                {
                  required: true,
                  message: `${variableLabels.singular} Key is required`
                },
                {
                  pattern: /^[A-Za-z0-9_-]+$/,
                  message: `${variableLabels.singular} name can only contain letters, numbers, dashes (-) or underscores (_)`,
                }
              ]}>
              <Input placeholder={`${variableLabels.singular} name`} />
            </Form.Item>
            <Form.Item name="img" label="Image">
              <Upload {...uploadProps} fileList={fileList}>
                <AntButton icon={<FontAwesomeIcon icon={solid("upload")} />}>Upload Image</AntButton>
              </Upload>
            </Form.Item>
          </Col>
        </Flex>

        <Form.Item name="description" label="Description">
          <TextArea cols={24} rows={5} placeholder={`${variableLabels.singular} Description`} />
        </Form.Item>

        <Form.Item name="type" label="Type" rules={[{ required: true, message: `${variableLabels.singular} Type is required` }]}>
          <Select placeholder={`${variableLabels.singular} Type`} options={environmentTypesOptions} />
        </Form.Item>

        <Form.Item noStyle dependencies={["type"]}>
          {({ getFieldValue }) => {
            const variableType = getFieldValue("type"), value = getFieldValue('value');
            if (!variableType) return;

            return (
              <>
                {variableType === 'json' && (
                  <Form.Item name="isSecret" label="Secret" valuePropName="checked" layout="horizontal" className="text-end">
                    <Switch />
                    {/* <span className="ms-2">Environment variable value will be treated as a private secret</span> */}
                  </Form.Item>
                )}
                <Form.Item name="value" label="Value" rules={getInputRules(variableType)}>
                  {variableType !== 'json'
                    ? renderInputComponent(variableType)
                    : (
                      <ScriptEditor
                        height={150}
                        value={value}
                        onChange={handleScriptChange}
                      />
                    )}
                </Form.Item>
              </>
            )
          }}
        </Form.Item>

        <Form.Item
          name="accessLevel"
          label="Access Level"
          rules={[{ required: true, message: `${variableLabels.singular} Access Level is required` }]}
        >
          <Select placeholder="Select privacy level" allowClear className="w-100">
            {accessLevelOptions.map((option, index) => (
              <Select.Option key={index} value={option.value} label={option.label}>
                <FontAwesomeIcon icon={option.icon} fixedWidth />
                <span className="ms-2">{option.label}</span>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="tags" label="Tags">
          <Select mode="tags" className="w-100" placeholder={`${variableLabels.singular} Tags`} />
        </Form.Item>
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <Button type="button" variant="light" size="sm" className="border" onClick={onCancel}>Close</Button>
        <Button type="submit" variant="success" size="sm">
          <FontAwesomeIcon icon={solid('save')} />
          <span className="ms-2">Save</span>
        </Button>
      </Modal.Footer>
    </Form>
  );
}